import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import * as XLSX from 'xlsx';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:5002' 
  : window.location.origin;

function AdminPanel() {
    // ✅ NAYA: User aur State nikal rahe hain
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const stateName = user.stateName || 'UP';
    const authHeaders = { headers: { 'x-state-name': stateName } };

    // --- THEME ---
    const colors = {
        primary: '#0d6efd',
        danger: '#dc3545',
        success: '#198754',
        warning: '#ffc107',
        info: '#0dcaf0',
        dark: '#212529',
        light: '#f8f9fa',
        white: '#ffffff',
        border: '#dee2e6',
        secondary: '#6c757d',
        bg: '#f3f4f6',
        cardBg: '#ffffff'
    };

    // --- STATES ---
    const [districts, setDistricts] = useState([]);
    const [branches, setBranches] = useState([]);
    const [pacsList, setPacsList] = useState([]);

    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedPacs, setSelectedPacs] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    const [stats, setStats] = useState({ total_pacs: 0, uploaded_pacs: 0 });
    const [pacsCounts, setPacsCounts] = useState(null);
    const [activeTable, setActiveTable] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    // --- INITIAL LOAD ---
    useEffect(() => {
        fetchGlobalStats();
        fetchDistricts();
    }, []);

    const fetchGlobalStats = () => {
        // ✅ NAYA: State header add kiya
        axios.get(`${API_BASE_URL}/api/admin/stats`, authHeaders)
            .then(res => setStats(res.data))
            .catch(err => console.error("Stats Fetch Error:", err));
    };

    const fetchDistricts = () => {
        // ✅ NAYA: State header add kiya
        axios.get(`${API_BASE_URL}/api/districts`, authHeaders)
            .then(res => {
                setDistricts(res.data.map(d => ({ value: d.district_id, label: d.district_name })));
            });
    };

    const handleSearch = (inputValue) => {
        setSearchQuery(inputValue);
        if (inputValue.length > 2) {
            // ✅ NAYA: State query me bheja aur header bhi add kiya
            axios.get(`${API_BASE_URL}/api/admin/search-pacs?q=${inputValue}&stateName=${stateName}`, authHeaders)
                .then(res => {
                    const options = res.data.map(p => ({
                        value: p.pacs_id,
                        label: `${p.pacs_name} (${p.pacs_code})`,
                        ...p
                    }));
                    setSearchResults(options);
                });
        } else {
            setSearchResults([]);
        }
    };

    const handleDirectSelect = (opt) => {
        if (!opt) return;
        setSelectedPacs(opt);
        setSearchQuery('');
        setSearchResults([]);
        handlePacsChange(opt);
    };

    const handleDistrictChange = (opt) => {
        setSelectedDistrict(opt); setSelectedBranch(null); setBranches([]); setSelectedPacs(null); setPacsList([]);
        // ✅ NAYA: State header add kiya
        if (opt) axios.get(`${API_BASE_URL}/api/branches/${opt.value}`, authHeaders).then(res => setBranches(res.data.map(b => ({ value: b.branch_id, label: b.branch_name }))));
    };

    const handleBranchChange = (opt) => {
        setSelectedBranch(opt); setSelectedPacs(null); setPacsList([]);
        // ✅ NAYA: State header add kiya
        if (opt) axios.get(`${API_BASE_URL}/api/pacs/${opt.value}`, authHeaders).then(res => setPacsList(res.data.map(p => ({ value: p.pacs_id, label: p.pacs_name }))));
    };

    const handlePacsChange = (opt) => {
        setSelectedPacs(opt);
        setPacsCounts(null);
        setTableData([]);
        setActiveTable(null);

        if (opt) {
            // ✅ NAYA: State body aur header mein add kiya
            axios.post(`${API_BASE_URL}/api/admin/pacs-counts`, { pacs_id: opt.value, stateName: stateName }, authHeaders)
                .then(res => {
                    setPacsCounts(res.data);
                    const firstKey = Object.keys(res.data).find(k => res.data[k] > 0);
                    if (firstKey) fetchTableData(firstKey, opt.value);
                });
        }
    };

    const fetchTableData = (tableName, pacsId) => {
        setActiveTable(tableName);
        setCurrentPage(1);
        setLoading(true);
        // ✅ NAYA: State body aur header mein add kiya
        axios.post(`${API_BASE_URL}/api/admin/fetch-data`, {
            pacs_id: pacsId || selectedPacs.value,
            table_name: tableName,
            stateName: stateName
        }, authHeaders).then(res => {
            setTableData(res.data.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    };

    const handleDeleteData = async () => {
        if (!selectedPacs) return;
        if (window.confirm(`WARNING: Delete all uploaded data for "${selectedPacs.label}"? (Master Data will remain safe)`)) {
            try {
                setLoading(true);
                // ✅ NAYA: State body aur header mein add kiya
                const res = await axios.post(`${API_BASE_URL}/api/admin/delete-data`, { pacs_id: selectedPacs.value, stateName: stateName }, authHeaders);
                if (res.data.success) {
                    alert("✅ Data Deleted Successfully!");
                    handlePacsChange(selectedPacs);
                    fetchGlobalStats();
                }
            } catch (err) {
                alert("❌ Delete Failed!");
            } finally {
                setLoading(false);
            }
        }
    };

    const downloadFullExcel = async () => {
        if (!selectedPacs) return alert("Please select a PACS first");
        const tablesToDownload = ['Membership', 'Savings', 'LandDetails', 'ShortTermLoans', 'LoanTransaction'];
        const wb = XLSX.utils.book_new();
        let hasData = false;

        for (const table of tablesToDownload) {
            try {
                // ✅ NAYA: State body aur header mein add kiya
                const res = await axios.post(`${API_BASE_URL}/api/admin/fetch-data`, {
                    pacs_id: selectedPacs.value,
                    table_name: table,
                    stateName: stateName
                }, authHeaders);
                if (res.data.data.length > 0) {
                    hasData = true;
                    const cleanData = res.data.data.map(({ pacs_id, ...rest }) => rest);
                    const ws = XLSX.utils.json_to_sheet(cleanData);
                    XLSX.utils.book_append_sheet(wb, ws, table);
                }
            } catch (err) { console.error(err); }
        }

        if (hasData) XLSX.writeFile(wb, `${selectedPacs.label}_FullData.xlsx`);
        else alert("No data available to download.");
    };

    // --- UI STYLES ---
    const s = {
        container: { fontFamily: "'Inter', sans-serif", backgroundColor: colors.bg, minHeight: '100vh', padding: '20px' },
        header: { backgroundColor: colors.cardBg, padding: '15px 30px', borderRadius: '12px', marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 15px rgba(0,0,0,0.04)' },
        brand: { fontSize: '1.5rem', fontWeight: '700', color: colors.dark },
        
        card: { backgroundColor: colors.white, borderRadius: '12px', padding: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)', marginBottom: '25px', border: `1px solid ${colors.border}` },

        statCard: (borderColor) => ({
            backgroundColor: colors.cardBg, borderRadius: '12px', padding: '20px', 
            borderLeft: `5px solid ${borderColor}`, boxShadow: '0 4px 10px rgba(0,0,0,0.03)',
            textAlign: 'center', flex: 1
        }),
        statTitle: { fontSize: '0.9rem', color: colors.secondary, fontWeight: '600', textTransform: 'uppercase' },
        statValue: { fontSize: '2rem', fontWeight: '700', color: colors.dark, margin: '5px 0' },

        searchBox: { background: colors.white, padding: '25px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', marginBottom: '30px', border: `1px solid ${colors.border}` },
        
        dataCard: (active, color) => ({
            padding: '15px 20px', borderRadius: '10px', cursor: 'pointer', minWidth: '140px',
            backgroundColor: active ? color : colors.white,
            color: active ? colors.white : colors.dark,
            border: `1px solid ${active ? color : colors.border}`,
            boxShadow: active ? '0 4px 10px rgba(0,0,0,0.1)' : 'none',
            textAlign: 'center', transition: 'all 0.2s ease', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }),

        table: { width: '100%', borderCollapse: 'collapse', backgroundColor: colors.white },
        th: { backgroundColor: colors.light, padding: '12px 15px', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', color: colors.secondary, borderBottom: `2px solid ${colors.border}`, whiteSpace: 'nowrap' },
        td: { padding: '10px 15px', borderBottom: `1px solid ${colors.border}`, fontSize: '0.9rem', color: colors.dark },

        btnSuccess: { backgroundColor: colors.success, color: colors.white, border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' },
        deleteBtn: { backgroundColor: colors.danger, color: colors.white, border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' },
        pageBtn: (disabled) => ({
            padding: '8px 16px', borderRadius: '6px', border: `1px solid ${colors.border}`,
            backgroundColor: disabled ? colors.light : colors.white,
            color: disabled ? colors.secondary : colors.primary,
            cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: '600'
        })
    };

    const customSelectStyles = {
        control: (base) => ({ ...base, borderRadius: '8px', padding: '2px', borderColor: colors.border, boxShadow: 'none', '&:hover': { borderColor: colors.primary } })
    };

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = tableData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(tableData.length / rowsPerPage);

    return (
        <div style={s.container}>
            <div style={s.header}>
                <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                    <div style={s.brand}>🛡️ View & Manage All Bulk Uploaded Data</div>
                    {/* 🆕 Cross-navigation to Monitoring Dashboard so Assam admins can switch
                        to the Super Admin / district view without going back to login. */}
                    <a href="/dashboard" style={{...s.statTitle, background: '#e0e7ff', color: '#4f46e5', padding: '6px 14px', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.85rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px'}}>
                        📊 Monitoring Dashboard
                    </a>
                    <a href="/upload" style={{...s.statTitle, background: '#fef3c7', color: '#92400e', padding: '6px 14px', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.85rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px'}}>
                        ⬆️ Bulk Upload
                    </a>
                </div>
                {/* ✅ NAYA: State Indicator Badge */}
                <div style={{background: '#e0e7ff', color: '#4f46e5', padding: '6px 15px', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.9rem'}}>
                    <i className="fa-solid fa-map-location-dot me-2"></i> State Context: {stateName}
                </div>
            </div>

            {/* ✅ FIXED STATS CARDS USAGE */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
                <div style={s.statCard(colors.primary)}>
                    <div style={s.statTitle}>Total Registered PACS</div>
                    <div style={s.statValue}>{stats.total_pacs}</div>
                </div>
                <div style={s.statCard(colors.success)}>
                    <div style={s.statTitle}>Data Uploaded</div>
                    <div style={{ ...s.statValue, color: colors.success }}>{stats.uploaded_pacs}</div>
                </div>
                <div style={s.statCard(colors.warning)}>
                    <div style={s.statTitle}>Pending Action</div>
                    <div style={{ ...s.statValue, color: colors.warning }}>{stats.total_pacs - stats.uploaded_pacs}</div>
                </div>
            </div>

            <div style={s.searchBox}>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '10px', display: 'block', color: colors.dark }}>🔍 Search PACS by Name (Direct)</label>
                    <Select
                        inputValue={searchQuery}
                        onInputChange={handleSearch}
                        onChange={handleDirectSelect}
                        options={searchResults}
                        placeholder="Type PACS name here..."
                        isClearable
                        styles={customSelectStyles}
                    />
                </div>
                <div style={{ textAlign: 'center', color: '#aaa', fontWeight: 'bold' }}>--- OR FILTER BY LOCATION ---</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginTop: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div><label style={{ fontSize: '0.85rem', fontWeight: '600', color: colors.secondary, marginBottom: '8px', display: 'block' }}>District</label><Select options={districts} value={selectedDistrict} onChange={handleDistrictChange} placeholder="Select District..." styles={customSelectStyles} /></div>
                    <div><label style={{ fontSize: '0.85rem', fontWeight: '600', color: colors.secondary, marginBottom: '8px', display: 'block' }}>Branch</label><Select options={branches} value={selectedBranch} onChange={handleBranchChange} placeholder="Select Branch..." isDisabled={!selectedDistrict} styles={customSelectStyles} /></div>
                    <div><label style={{ fontSize: '0.85rem', fontWeight: '600', color: colors.secondary, marginBottom: '8px', display: 'block' }}>PACS</label><Select options={pacsList} value={selectedPacs} onChange={handlePacsChange} placeholder="Select PACS..." isDisabled={!selectedBranch} styles={customSelectStyles} /></div>
                </div>
            </div>

            {selectedPacs && pacsCounts && (
                <div style={s.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.border}`, paddingBottom: '15px', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0, color: colors.dark, fontSize: '1.25rem' }}>Data for: <span style={{ color: colors.primary }}>{selectedPacs.label}</span></h3>
                        <button onClick={handleDeleteData} style={s.deleteBtn}>🗑️ Delete Uploaded Data</button>
                    </div>

                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '20px', marginBottom: '25px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '20px' }}>
                        {pacsCounts.Membership > 0 && <div style={s.dataCard(activeTable === 'Membership', colors.primary)} onClick={() => fetchTableData('Membership')}><div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{pacsCounts.Membership}</div><div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Members</div></div>}
                        {pacsCounts.Savings > 0 && <div style={s.dataCard(activeTable === 'Savings', colors.success)} onClick={() => fetchTableData('Savings')}><div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{pacsCounts.Savings}</div><div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Savings</div></div>}
                        {pacsCounts.LandDetails > 0 && <div style={s.dataCard(activeTable === 'LandDetails', colors.danger)} onClick={() => fetchTableData('LandDetails')}><div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{pacsCounts.LandDetails}</div><div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Land Records</div></div>}
                        {pacsCounts.ShortTermLoans > 0 && <div style={s.dataCard(activeTable === 'ShortTermLoans', colors.warning)} onClick={() => fetchTableData('ShortTermLoans')}><div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{pacsCounts.ShortTermLoans}</div><div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Loans</div></div>}
                        {pacsCounts.LoanTransaction > 0 && <div style={s.dataCard(activeTable === 'LoanTransaction', colors.info)} onClick={() => fetchTableData('LoanTransaction')}><div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{pacsCounts.LoanTransaction}</div><div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Transactions</div></div>}
                    </div>

                    {loading ? <div style={{ textAlign: 'center', padding: '50px', color: colors.secondary }}><i className="fa-solid fa-spinner fa-spin fa-2x"></i><br />Loading Data...</div> : tableData.length > 0 ? (
                        <>
                            <div style={s.tableContainer}>
                                <table style={s.table}>
                                    <thead><tr>{Object.keys(tableData[0]).map(key => <th key={key} style={s.th}>{key.replace(/_/g, ' ')}</th>)}</tr></thead>
                                    <tbody>
                                        {currentRows.map((row, i) => (
                                            <tr key={i} style={{ backgroundColor: i % 2 === 0 ? colors.white : '#f9f9f9' }}>
                                                {Object.values(row).map((val, j) => <td key={j} style={s.td}>{val}</td>)}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
                                <button onClick={downloadFullExcel} style={s.btnSuccess}>⬇️ Download All Excel</button>
                                <div style={s.pagination}>
                                    <button style={s.pageBtn(currentPage === 1)} onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>Prev</button>
                                    <span style={{ display: 'flex', alignItems: 'center', fontWeight: '600', color: colors.dark }}>Page {currentPage} of {totalPages}</span>
                                    <button style={s.pageBtn(currentPage === totalPages)} onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>Next</button>
                                </div>
                            </div>
                        </>
                    ) : <div style={{ textAlign: 'center', padding: '30px', color: colors.secondary, border: `2px dashed ${colors.border}`, borderRadius: '12px' }}>Select a category above to view detailed records.</div>}
                </div>
            )}
        </div>
    );
}

export default AdminPanel;
